import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const API_VERSION = import.meta.env.VITE_API_VERSION || '/api/v1'

// Shared across clients so parallel 401s trigger a single refresh call
let refreshPromise = null

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) throw new Error('No refresh token')
  const response = await axios.post(`${API_BASE_URL}/auth-service${API_VERSION}/auth/refresh`, { refreshToken })
  localStorage.setItem('token', response.data.accessToken)
  return response.data.accessToken
}

export const clearSession = () => {
  for (const key of ['token', 'refreshToken', 'user', 'tenant']) localStorage.removeItem(key)
}

/**
 * One axios instance per backend service, routed through the API gateway:
 * `${VITE_API_BASE_URL}/<service context>/api/v1`.
 */
const createApiClient = (serviceName) => {
  const apiClient = axios.create({
    baseURL: `${API_BASE_URL}${serviceName}${API_VERSION}`,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  // On 401, try one token refresh, then replay the request; otherwise back to login
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config
      if (error.response?.status === 401 && original && !original._retried && !original.skipAuthRedirect) {
        original._retried = true
        try {
          refreshPromise = refreshPromise || refreshAccessToken()
          const token = await refreshPromise
          original.headers.Authorization = `Bearer ${token}`
          return apiClient(original)
        } catch {
          clearSession()
          window.location.href = '/login'
        } finally {
          refreshPromise = null
        }
      }
      return Promise.reject(error)
    }
  )

  return apiClient
}

export default createApiClient
